import { useContext, useMemo } from 'react';
import { AuthContext } from '@@/contexts/AuthContext';
import { CASES_PERMISSIONS, CASES_ROLES } from '../constant/roles';
import { useGetUserRolePermissions } from '@@/hooks/useGetUserRolePermissions';

export type CasesUserRole = (typeof CASES_ROLES)[keyof typeof CASES_ROLES] | null;

interface UseCasesUserRoleReturn {
  role: CasesUserRole;
  isPronetxCaseAdmin: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  hasAdminAccess: boolean;
  userPermissions: string[];
  hasCasesAccess: boolean;
}

export const useCasesUserRole = (permissions?: string[]): UseCasesUserRoleReturn => {
  const { instanceData } = useContext(AuthContext);
  const { permissions: globalPermissions } = useGetUserRolePermissions();

  return useMemo(() => {
    const userPermissions =
      permissions || [...(globalPermissions?.globalPermissions || []), ...(instanceData?.permissions || [])] || [];
    const isPronetxCaseAdmin = userPermissions.includes(CASES_PERMISSIONS.ALL_CASES_ADMIN);
    const isAdmin = userPermissions.includes(CASES_PERMISSIONS.COMPANY_CASES_ADMIN);
    const isCustomer = userPermissions.includes(CASES_PERMISSIONS.MY_CASES_READER);

    let role: CasesUserRole = null;

    if (isPronetxCaseAdmin) {
      role = CASES_ROLES.ALL_CASES_PERMISSION;
    } else if (isAdmin) {
      role = CASES_ROLES.COMPANY_CASES_PERMISSION;
    } else if (isCustomer) {
      role = CASES_ROLES.MY_CASES_PERMISSION;
    } else {
      role = CASES_ROLES.NO_ACCESS;
    }

    const hasAdminAccess = isPronetxCaseAdmin || isAdmin;
    const hasCasesAccess = hasAdminAccess || isCustomer;

    return {
      role,
      isPronetxCaseAdmin,
      isAdmin,
      isCustomer,
      hasAdminAccess,
      userPermissions,
      hasCasesAccess
    };
  }, [permissions, globalPermissions, instanceData?.permissions]);
};

export default useCasesUserRole;
